import { useDispatch, useSelector } from "react-redux";
import { addContextSection, editContextSectionDescription, editContextSectionHeading, editContextTitle, getContextOfLastClickedItem, getCurrentDashboardMode, removeContextSection } from "src/store/dashboardSlice";
import EditableText from "../common/EditableText";
import { PlusIcon, Trash } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { naruhoneStyles } from "src/styles/naruhoneStyles";
import { ItemType } from "./WidgetTypes";
import StackIcon from "tech-stack-icons";



interface DashboardItemContext {
    title?: string;
    sections?: {
        heading?: string;
        description?: string;
        imageUrls?: string[];
    }[];
}


const contextTitleStyle = `${naruhoneStyles.h1Text} text-3xl lg:text-5xl`
const contextSectionHeadingStyle = `${naruhoneStyles.h2Text} text-lg`;
const contextSectionDescriptionStyle = `${naruhoneStyles.pText}`;

interface ContextWidgetProps {
    containerRef: React.RefObject<HTMLDivElement>;
}

const ContextWidget = ({containerRef}:ContextWidgetProps) => {
    const newContext = useSelector(getContextOfLastClickedItem) as DashboardItemContext;
    const dispatch = useDispatch();
    const dashboardMode = useSelector(getCurrentDashboardMode);
    const [context, setContext] = useState(newContext);
    const [didMakeEdit, setDidMakeEdit] = useState(false);

    const videoAdminRef = useRef<HTMLVideoElement>(null);
    const videoChatRef = useRef<HTMLVideoElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState<number>(400);

    const [fadeClass, setFadeClass] = useState('opacity-0'); // start hidden

    useEffect(() => {
        //If we just made an edit you dont need to replay the animation.
        if (didMakeEdit === true) {
            setDidMakeEdit(false);
            setContext(newContext);
            return;
        }
        // Step 1: Instantly hide without transition
        setFadeClass('opacity-0');
        
        
        // Step 2: Next tick — re-add transition + fade-in + set the context data to avoid the race condition
        const timeout = setTimeout(() => {
            setContext(newContext);
            setFadeClass('opacity-100 transition-opacity duration-200 ease-in');
            if(scrollRef.current){
            scrollRef.current.scrollTop = 0;
        }
            
        }, 100);

        return () => clearTimeout(timeout);
    }, [newContext]);

    useEffect(() => {
        const updateHeight = () => {
           if (containerRef?.current && containerRef.current.offsetHeight) {
                setContainerHeight(Math.min(400, containerRef.current.offsetHeight));
            } else {
                setContainerHeight(400);
            }
        };
        setTimeout(() => {updateHeight();}, 1000);
        
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [containerRef]);



    const drawSection = (section: any, index: number) => {
        return <div className="mb-2 text-gray-200">
            {dashboardMode === "edit" && <button onClick={(e) => { dispatch(removeContextSection({ sectionIndex: index })); }
            }>
                <Trash size={16} />
            </button>}
            <EditableText
                text={section.heading as string}
                onChange={(newHeading) => {
                    dispatch(editContextSectionHeading({ newHeading, sectionIndex: index }));
                    if (section.heading !== newHeading)
                        setDidMakeEdit(true);
                }}
                className={contextSectionHeadingStyle}
                placeholder="Enter a heading"
                isEditable={dashboardMode === "edit"}
            />
            <EditableText
                text={section?.description as string}
                onChange={(newDescription) => {
                    dispatch(editContextSectionDescription({ newDescription, sectionIndex: index }));
                    if (section.description !== newDescription)
                        setDidMakeEdit(true);
                }}
                className={`${contextSectionDescriptionStyle} ${section?.description ? 'my-4' : ''}`}
                placeholder="Enter a description"
                isEditable={dashboardMode === "edit"}
            />
            {section.imageUrls && section.imageUrls?.map((imageUrl: string, i: any) => {
                return <img key={imageUrl + i} src={imageUrl} alt={"test"} />
            })}
        </div>
    }

    if(newContext === null){
        return <>
        <div className={`${fadeClass}  h-full flex flex-col text-gray-200`}>
            <div className="my-2 px-4">
                <p>Select a widget for more info</p>
                
            </div>
            </div>
        </>
    }
    return <>
        <div ref={scrollRef} className={`${fadeClass}  h-full flex flex-col text-gray-200 overflow-y-auto`}>
            <div className="my-2 px-4">
                <EditableText
                    text={context?.title as string}
                    onChange={(newTitle) => {
                        dispatch(editContextTitle({ newTitle }));
                        if (context?.title !== newTitle)
                            setDidMakeEdit(true);
                    }}
                    className={`${contextTitleStyle} ${context?.title ? 'mb-4' : ''}  shrink-0`}
                    placeholder="Enter a title"
                    isEditable={dashboardMode === "edit"}
                />
            </div>

            <div className=" px-4">
                {context?.sections?.map((item, i) => {
                    return <div key={i + 'aafeafae'}>
                        {drawSection(item, i)}
                        {newContext?.title?.toLowerCase() === "resume dashboard" && i === 0 && <>
                            <div className="flex ">
                                <div className="mb-2">
                                    {[
                                        { title: "Node.js", icon: "nodejs" },
                                        { title: "React", icon: "reactjs" },
                                        { title: "Firebase", icon: "firebase" },
                                        { title: "Flutter", icon: "flutter" },
                                        { title: "Postman", icon: "postman" },
                                        { title: "VScode", icon: "vscode" },
                                        { title: "Github", icon: "github" },
                                    ].map((ic, index) => {
                                        return (
                                            <span key={ic.icon + index} title={ic.title}>
                                                <StackIcon
                                                    className="w-8 h-8 mr-2"
                                                    name={ic.icon.toLowerCase()}

                                                />
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="my-4 transition-transform duration-500 ease-in-out transform group-hover:scale-105">
                                <video
                                    ref={videoAdminRef}
                                    src="/video/naruhone-admincomp.mp4"
                                    autoPlay
                                    preload="auto"
                                    muted
                                    loop
                                    playsInline
                                    className="rounded-xl shadow-lg object-contain w-full"
                                    style={{ maxHeight: `${containerHeight}px` }}
                                    onCanPlay={() => videoAdminRef.current?.play()}
                                />
                            </div>

                        </>}
                        {newContext?.title?.toLowerCase() === "habit tracker" && i === 0 && <>
                            <div className="my-4 transition-transform duration-500 ease-in-out transform group-hover:scale-105">
                                <video
                                    ref={videoChatRef}
                                    src="/video/naruhone-gym-comp2.mp4"
                                    autoPlay
                                    preload="auto"
                                    muted
                                    loop
                                    playsInline
                                    className="rounded-xl shadow-lg object-contain w-full"
                                    style={{ maxHeight: `${containerHeight}px` }}
                                    onCanPlay={() => videoChatRef.current?.play()}
                                />
                            </div>
                        </>}
                        {newContext?.title?.toLowerCase() === "public chat" && i === 0 && <>
                            <div className="my-4 transition-transform duration-500 ease-in-out transform group-hover:scale-105">
                                <video
                                    ref={videoChatRef}
                                    src="/video/naruhone-chat-comp.mp4"
                                    autoPlay
                                    preload="auto"
                                    muted
                                    loop
                                    playsInline
                                    className="rounded-xl shadow-lg object-contain w-full"
                                    style={{ maxHeight: `${containerHeight}px` }}
                                    onCanPlay={() => videoChatRef.current?.play()}
                                />
                            </div>
                        </>}

                    </div>
                })}

                {dashboardMode === "edit" && context && <div className="flex items-center gap-1 text-gray-200"
                    onClick={() => {
                        setDidMakeEdit(true);
                        dispatch(addContextSection({}))
                    }}>
                    <PlusIcon size={16} />
                    <p>
                        Add section
                    </p>
                </div>
                }
            </div>
        </div>
    </>
}

export default ContextWidget;